import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DropdownTreeMenu } from 'fc-ui-components';
import { AutoUnsubscribe } from 'helpers';
import * as _ from 'lodash';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { ProductSearchService, SearchParameters } from 'shared/services';
import { DetailedSizes } from 'shared/services/product-search/facets-ui-mapper';
import { ReorderSearch } from 'shared/services/product-search/product-search.service';
import { SearchParametersResult } from '../../../b2b-order/retailer-platform/order/shop/search/search.component';

@AutoUnsubscribe
@Component({
    selector: 'fc-detailed-size-filter',
    templateUrl: './detailed-size-filter.component.html',
    styleUrls: ['./detailed-size-filter.component.less'],
    standalone: false
})
export class DetailedSizeFilterComponent implements OnInit {
    @Input() public formGroup: FormGroup;
    @Input() public applySearchParameters: (searchParameters: Partial<SearchParameters>) => void;

    public search: Observable<ReorderSearch>;
    public detailedSizesFilter: Observable<DropdownTreeMenu>;
    public sizesFilterValue: Observable<string[]>;
    public disabled = false;
    public detailedSizesFilterSubscription: Subscription;
    constructor(
        private activatedRoute: ActivatedRoute,
        private productSearchService: ProductSearchService
    ) {}

    ngOnInit() {
        Object.values(DetailedSizes).forEach((detailedSize) => {
            if (!this.formGroup.controls[detailedSize]) {
                this.formGroup.addControl(detailedSize, new FormControl([]));
            }
        });
        const searchParameters: Observable<SearchParameters> = this.activatedRoute.queryParamMap.pipe(
            map((paramMap) => this.productSearchService.mapUrlParams(paramMap))
        );

        this.search = combineLatest([this.productSearchService.getReorderSearchResult(), searchParameters]).pipe(
            map(([searchResults, searchParams]) => searchResults)
        );

        const isSearchResultWithoutErrors = (result): result is SearchParametersResult[] => {
            const [, searchResult] = result;
            return !searchResult.result.error;
        };

        this.detailedSizesFilter = combineLatest([searchParameters, this.search]).pipe(
            filter(isSearchResultWithoutErrors),
            switchMap(([{ filters }, { result }]) => {
                Object.values(DetailedSizes).forEach((detailedSize) => {
                    this.formGroup.controls[detailedSize].setValue(
                        filters[detailedSize]?.map((value) => `${detailedSize}:${value}`) ?? []
                    );
                });
                return this.productSearchService.mapFacetToDetailedSizesFilter(result.facets, filters);
            }),
            shareReplay()
        );

        this.detailedSizesFilterSubscription = this.detailedSizesFilter.subscribe((detailedSizesFilter) => {
            this.disabled = _.every(detailedSizesFilter.sections, (section) => _.isEmpty(section.items));
        });

        // Initialize Observable property for sizes filter value to fix signal compatibility
        this.sizesFilterValue = combineLatest([
            this.formGroup.controls.generalSizes.valueChanges.pipe(
                startWith(this.formGroup.controls.generalSizes.value || [])
            ),
            this.formGroup.controls.widths.valueChanges.pipe(startWith(this.formGroup.controls.widths.value || [])),
            this.formGroup.controls.lengths.valueChanges.pipe(startWith(this.formGroup.controls.lengths.value || []))
        ]).pipe(
            map(([generalSizes, widths, lengths]) => [...generalSizes, ...widths, ...lengths]),
            distinctUntilChanged(),
            shareReplay(1)
        );
    }

    public saveDetailedSizeFilter(formProperty: string, selectedValues: string[]) {
        if (_.isEmpty(selectedValues)) {
            this.formGroup.controls[formProperty].setValue([]);
        } else {
            this.formGroup.controls[formProperty].setValue(
                selectedValues.map((value) => `${formProperty}:${value}`) ?? []
            );
        }
    }

    public handleOnSelectDetailedSizeFilter(selectedValues: string[]) {
        const valuesByKeys = this.parseDetailedSizesSelectedItems(selectedValues);
        Object.entries(valuesByKeys).forEach(([propertyKey, values]) =>
            this.saveDetailedSizeFilter(propertyKey, values)
        );
        this.applyDetailedSizesFilter.bind(this)(selectedValues);
    }

    public parseDetailedSizesSelectedItems(sizes: string[]) {
        return sizes.reduce(
            (acc, filterValue) => {
                const [key, value] = filterValue.split(':');
                const { [key]: filter } = acc;
                filter.push(value);
                return { ...acc, [key]: filter };
            },
            { widths: [], lengths: [], generalSizes: [] } as Record<string, string[]>
        );
    }
    public applyDetailedSizesFilter(sizes: string[]): void {
        this.applySearchParameters({ filters: this.parseDetailedSizesSelectedItems(sizes) });
    }
}
